import { SpreadsheetGridRow } from "@components/spreadsheet/SpreadsheetGridRow";
import classNames from "classnames";
import { isFunction } from "@common/helpers/typeGuards";
import { range } from "@common/helpers/collections";
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import styles from "./SpreadsheetGrid.module.scss";
import {
    CellValue,
    ColumnDef,
    ColumnPinInfo,
    FillMode,
    FlatRow,
    PINNED_CELL_Z_INDEX,
    SelectionRange,
    SpreadsheetAppearance,
    SpreadsheetCell,
    SpreadsheetData,
    SpreadsheetRowData,
} from "./types";
import { flattenRows, getExpandableRowIds } from "./spreadsheetUtils";
import { SpreadsheetFloatingFooterHost } from "./SpreadsheetFloatingFooterHost";
import { SpreadsheetTreeToggle } from "./SpreadsheetTreeCellContent";
import { useSpreadsheetScrollEdges } from "./useSpreadsheetScrollEdges";
import {
    getCenteredColumnTrack,
    withCenteringFillerTracks,
} from "./centeredColumns";
import {
    clampSelectableColumnIndex,
    findNextSelectableColumnIndex,
    getLastSelectableColumnIndex,
    isSpreadsheetActionsColumn,
} from "./spreadsheetActionsColumn";
import { SpreadsheetColumnResizeScope } from "./SpreadsheetColumnResizeProvider";
import { useSpreadsheetCopyPaste } from "./useSpreadsheetCopyPaste";
import {
    canResizeSpreadsheetHeader,
    getSpreadsheetColumnResizeId,
    useSpreadsheetColumnResize,
} from "./useSpreadsheetColumnResize";
import { useSpreadsheetHistory } from "./useSpreadsheetHistory";
import { getRangeActionCell, SpreadsheetRangeAction } from "./rangeAction";

// Pixel delta from mousedown origin before a hold becomes a drag-select. Matches the
// macOS native drag threshold and prevents scroll-induced `mouseenter` events (which
// fire as cells slide under a stationary cursor) from being misread as user drags.
const DRAG_THRESHOLD_PX = 3;

/**
 * Compute sticky pin metadata for all columns.
 * Columns are always rendered in the order: pinned-left → center → pinned-right,
 * so `colIdx` here refers to position in the **original** columns array passed by
 * the caller. We return a Map<original-index → ColumnPinInfo>.
 */
function pinnedColumnWidthPx(
    column: ColumnDef,
    columnIndex: number,
    columnSizing: Record<string, number>,
): number {
    return (
        columnSizing[getSpreadsheetColumnResizeId(column, columnIndex)] ??
        column.width ??
        0
    );
}

function buildColumnPinInfo({
    columns,
    columnSizing,
}: {
    columns: ColumnDef[];
    columnSizing: Record<string, number>;
}): Map<number, ColumnPinInfo> {
    const pinnedLeft = columns
        .map((c, i) => ({ col: c, idx: i }))
        .filter(({ col }) => col.pinned === "left");
    const pinnedRight = columns
        .map((c, i) => ({ col: c, idx: i }))
        .filter(({ col }) => col.pinned === "right");

    const info = new Map<number, ColumnPinInfo>();

    // Accumulate left offsets
    let leftOffset = 0;
    for (const [i, { col, idx }] of pinnedLeft.entries()) {
        const isLastPinnedLeft = i === pinnedLeft.length - 1;
        info.set(idx, {
            isPinnedLeft: true,
            isPinnedRight: false,
            isLastPinnedLeft,
            isFirstPinnedRight: false,
            pinnedStyle: {
                position: "sticky",
                left: leftOffset,
                zIndex: PINNED_CELL_Z_INDEX,
            },
        });
        leftOffset += pinnedColumnWidthPx(col, idx, columnSizing);
    }

    // Accumulate right offsets (reversed)
    let rightOffset = 0;
    for (const { col, idx } of [...pinnedRight].reverse()) {
        const isFirstPinnedRight = idx === pinnedRight[0]?.idx;
        info.set(idx, {
            isPinnedLeft: false,
            isPinnedRight: true,
            isLastPinnedLeft: false,
            isFirstPinnedRight,
            pinnedStyle: {
                position: "sticky",
                right: rightOffset,
                zIndex: PINNED_CELL_Z_INDEX,
            },
        });
        rightOffset += pinnedColumnWidthPx(col, idx, columnSizing);
    }

    // Fill unpinned columns
    columns.forEach((_, i) => {
        if (info.has(i)) {
            return;
        }
        info.set(i, {
            isPinnedLeft: false,
            isPinnedRight: false,
            isLastPinnedLeft: false,
            isFirstPinnedRight: false,
            pinnedStyle: {},
        });
    });

    return info;
}

/**
 * Build gridTemplateColumns from column widths when widths are specified.
 * Falls back to `1fr` for columns without a width.
 */
function buildGridTemplateColumns(columns: ColumnDef[]): string {
    return columns
        .map((c) => {
            if (!c.width) {
                return "1fr";
            }
            // Pinned columns must be exact so sticky offset math is correct.
            // Non-pinned columns with a width use minmax so they can grow to
            // fill the remaining container width (keeps the grid full-width).
            return c.pinned ? `${c.width}px` : `minmax(${c.width}px, 1fr)`;
        })
        .join(" ");
}

export type ChangedCells = Array<[rowIdx: number, colIdx: number]>;
export interface SpreadsheetGridHandle {
    focus(options?: { row?: number; col?: number }): void;
}

interface SpreadsheetGridProps {
    data: SpreadsheetData;
    columns: ColumnDef[];
    onChange?: (newData: SpreadsheetData, changedCells: ChangedCells) => void;
    onSelectedCellChange?: (cell: SpreadsheetCell | null) => void;
    /**
     * Called whenever the selection range (multi-cell) changes.
     * Provides both the anchor cell and the extent of the selection.
     */
    onSelectionRangeChange?: (
        selectedCell: SpreadsheetCell | null,
        selectionRange: SelectionRange,
    ) => void;
    className?: string;
    rowClassName?: string;
    getRowClassName?: (rowIdx: number) => string | undefined;
    noOuterBorder?: boolean;
    gridTemplateColumns?: string;
    minSpareRows?: number;
    emptySpareRows?: boolean;
    isCellEditable?: (rowIdx: number, colIdx: number) => boolean;
    stickyHeader?: boolean;
    hideHeaderRow?: boolean;
    showTotalsRow?: boolean;
    disableDragFill?: boolean;
    onExitEditMode?: () => void;
    onEnterEditMode?: () => void;
    /** Highlight entire row on hover with blue background */
    rowHoverHighlight?: boolean;
    /** Use white background for non-editable cells instead of gray */
    lightNotEditableCells?: boolean;
    /** Show dropdown indicator arrows in dropdown cells */
    showDropdownIndicator?: boolean;
    prependHeader?: React.ReactNode;
    /** Overflow behaviour for the grid wrapper. Defaults to "hidden". Set to "auto" when the grid is inside a scroll container. */
    overflow?: "hidden" | "auto" | "scroll";
    /** Enable expand/collapse rows for tree data. */
    expandable?: boolean;
    /** Row IDs that should be expanded on first render. */
    defaultExpandedIds?: string[];
    /** Called when expanded row set changes. */
    onExpandedChange?: (expandedIds: string[]) => void;
    /** Pixels to indent per nesting level. Defaults to 16. */
    nestingIndentPx?: number;
    /** Whether the grid is readonly controls styling and sets disabled to true. */
    readonly?: boolean;
    /** When false, blur does not clear selected cell/range. */
    clearSelectionOnBlur?: boolean;
    /** Called when a row is clicked. */
    onRowClick?: (rowIdx: number) => void;
    /** Called when ArrowDown is pressed on the last row. Allows parent to move focus to a subsequent grid. Receives the current column index. */
    onNavigatePastEnd?: (col: number) => void;
    /** Called when ArrowUp is pressed on the first row. Allows parent to move focus to a preceding grid. Receives the current column index. */
    onNavigatePastStart?: (col: number) => void;
    enableColumnResizing?: boolean;
    /** Imperative handle ref for programmatic focus control. */
    gridHandle?: React.Ref<SpreadsheetGridHandle>;
    /** Shows a button on the selected range: a click runs the action, a drag carries the range out of the grid. */
    rangeAction?: SpreadsheetRangeAction;
    /** Visual style; defaults to `"spreadsheet"`. */
    appearance?: SpreadsheetAppearance;
    /**
     * Floats over the grid's bottom edge (a command island). The grid then
     * fills its parent and scrolls itself, with room past the last row.
     */
    floatingFooter?: React.ReactNode;
    /**
     * Centers the columns when they're narrower than the grid, like
     * ViewTable's `centerColumns`: each column stops at its `maxSize` (or
     * `width`) and filler tracks split the rest, while row lines, the row
     * hover and the header rule still run the full width.
     */
    centerColumns?: boolean;
}

const SpreadsheetGridContent: React.FC<SpreadsheetGridProps> = ({
    data,
    columns,
    onChange,
    onSelectedCellChange,
    onSelectionRangeChange,
    className,
    rowClassName,
    getRowClassName,
    noOuterBorder,
    gridTemplateColumns,
    minSpareRows,
    emptySpareRows,
    isCellEditable,
    stickyHeader,
    hideHeaderRow,
    showTotalsRow,
    disableDragFill,
    onExitEditMode,
    onEnterEditMode,
    rowHoverHighlight,
    lightNotEditableCells,
    showDropdownIndicator,
    prependHeader,
    overflow = "hidden",
    expandable = false,
    defaultExpandedIds,
    onExpandedChange,
    readonly = false,
    nestingIndentPx = 16,
    clearSelectionOnBlur = true,
    onRowClick,
    onNavigatePastEnd,
    onNavigatePastStart,
    enableColumnResizing = true,
    gridHandle,
    rangeAction,
    appearance = "spreadsheet",
    floatingFooter,
    centerColumns = false,
}) => {
    const handleInnerChange = useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            if (isCellEditable) {
                const hasDisabledCell = changedCells.some(
                    ([rowIdx, colIdx]) => !isCellEditable(rowIdx, colIdx),
                );

                if (hasDisabledCell) {
                    return;
                }
            }

            onChange?.(newData, changedCells);
        },
        [isCellEditable, onChange],
    );

    const { handleChange, handleRedo, handleUndo } = useSpreadsheetHistory({
        onChange: handleInnerChange,
        initialData: data,
    });

    const [selectedCell, setSelectedCell] = useState<SpreadsheetCell | null>(
        null,
    );
    const [editingCell, setEditingCell] = useState<SpreadsheetCell | null>(
        null,
    );
    const [selectionRange, setSelectionRange] = useState<SelectionRange>({
        start: null,
        end: null,
        selecting: false,
    });
    const [fillMode, setFillMode] = useState<FillMode>({
        isFilling: false,
        start: null,
        end: null,
    });
    const fillModeRef = useRef(fillMode);
    const gridRef = useRef<HTMLDivElement | null>(null);
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const attachGridElement = useCallback((element: HTMLDivElement | null) => {
        gridRef.current = element;
        setGridElement(element);
    }, []);

    const defaultGridTemplateColumns = useMemo(() => {
        if (gridTemplateColumns) {
            return gridTemplateColumns;
        }
        if (centerColumns) {
            return columns.map(getCenteredColumnTrack).join(" ");
        }
        if (columns.some((column) => column.width !== undefined)) {
            return buildGridTemplateColumns(columns);
        }
        return `repeat(${columns.length}, 1fr)`;
    }, [gridTemplateColumns, centerColumns, columns]);

    const {
        resizeTable,
        resetColumnWidth,
        columnSizing,
        gridTemplateColumns: resizedGridTemplateColumns,
    } = useSpreadsheetColumnResize({
        columns,
        gridRef,
        defaultGridTemplateColumns,
        enableColumnResizing,
    });
    const gridTrackColumns = centerColumns
        ? withCenteringFillerTracks(resizedGridTemplateColumns)
        : resizedGridTemplateColumns;
    const isTrackingScrollEdges =
        appearance !== "spreadsheet" || floatingFooter !== undefined;
    const scrollbarSizes = useSpreadsheetScrollEdges({
        grid: isTrackingScrollEdges ? gridElement : null,
        columnLayout: gridTrackColumns,
    });

    const mouseDownRef = useRef(false);
    const mouseDownPositionRef = useRef<{ x: number; y: number } | null>(null);
    const hasDraggedRef = useRef(false);
    const [editedCellValue, setEditedCellValue] = useState<any>();
    const [enterEditModeKey, setEnterEditModeKey] = useState<string | null>(
        null,
    );
    const cellRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());

    const [expandedIds, setExpandedIds] = useState<Set<string>>(
        () => new Set(defaultExpandedIds ?? []),
    );

    useEffect(() => {
        if (defaultExpandedIds) {
            setExpandedIds(new Set(defaultExpandedIds));
        }
    }, [defaultExpandedIds]);

    const toggleExpanded = useCallback(
        (id: string) => {
            setExpandedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                onExpandedChange?.(Array.from(next));
                return next;
            });
        },
        [onExpandedChange],
    );

    const flatData = useMemo(() => {
        if (expandable) {
            return flattenRows(data as SpreadsheetRowData[], {
                expandedIds,
                depth: 0,
                parentId: "",
            });
        }
        return data as FlatRow[];
    }, [expandable, data, expandedIds]);

    const hasTreeHeaderLead = expandable && appearance !== "spreadsheet";
    const isSomeRowExpanded = expandedIds.size > 0;
    const toggleAllExpanded = useCallback(() => {
        const next = isSomeRowExpanded
            ? new Set<string>()
            : getExpandableRowIds(data);
        setExpandedIds(next);
        onExpandedChange?.(Array.from(next));
    }, [isSomeRowExpanded, data, onExpandedChange]);

    const { handleCopy, handlePaste } = useSpreadsheetCopyPaste({
        data,
        visibleRows: flatData,
        columns,
        selectedCell,
        selectionRange,
        isEditing: !!editingCell,
        onChange: handleChange,
        emptySpareRows,
    });

    const renderedData = useMemo(() => {
        if (minSpareRows) {
            return [...flatData, ...Array(minSpareRows).fill({})];
        }
        return flatData;
    }, [flatData, minSpareRows]);

    const columnPinInfo = useMemo(() => {
        if (process.env.NODE_ENV === "development") {
            for (const col of columns) {
                if (col.pinned && col.width === undefined) {
                    // eslint-disable-next-line no-console
                    console.warn(
                        `SpreadsheetGrid: column "${col.field}" is pinned but has no width. Pinned columns require an explicit width for correct sticky offset computation.`,
                    );
                }
            }
        }
        return buildColumnPinInfo({ columns, columnSizing });
    }, [columns, columnSizing]);

    const hasPinnedColumns = useMemo(
        () => columns.some((c) => c.pinned === "left" || c.pinned === "right"),
        [columns],
    );

    useEffect(() => {
        fillModeRef.current = fillMode;
    }, [fillMode]);

    useEffect(() => {
        onSelectedCellChange?.(selectedCell);
    }, [selectedCell, onSelectedCellChange]);

    // Debounce the selection range change callback so that intermediate drag
    // events (mouse-enter on every cell) don't fire expensive consumer logic
    // on every frame. The callback fires once the selection settles for 50ms.
    const selectionRangeChangeTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    useEffect(() => {
        if (!onSelectionRangeChange) {
            return;
        }
        if (selectionRangeChangeTimerRef.current) {
            clearTimeout(selectionRangeChangeTimerRef.current);
        }
        selectionRangeChangeTimerRef.current = setTimeout(() => {
            onSelectionRangeChange(selectedCell, selectionRange);
        }, 50);
        return () => {
            if (selectionRangeChangeTimerRef.current) {
                clearTimeout(selectionRangeChangeTimerRef.current);
            }
        };
    }, [selectedCell, selectionRange, onSelectionRangeChange]);

    // Compute total width of pinned-left and pinned-right columns so the
    // custom scroll function can offset for them.
    const pinnedLeftWidth = useMemo(
        () =>
            columns.reduce(
                (sum, column, columnIndex) =>
                    column.pinned === "left"
                        ? sum +
                          pinnedColumnWidthPx(column, columnIndex, columnSizing)
                        : sum,
                0,
            ),
        [columns, columnSizing],
    );
    const pinnedRightWidth = useMemo(
        () =>
            columns.reduce(
                (sum, column, columnIndex) =>
                    column.pinned === "right"
                        ? sum +
                          pinnedColumnWidthPx(column, columnIndex, columnSizing)
                        : sum,
                0,
            ),
        [columns, columnSizing],
    );

    // Latest pin layout captured in a ref so the scroll effect below only
    // re-fires when the selection actually moves — not whenever columns
    // identity churns, which would call scrollIntoView on a stale selected
    // cell and yank the scroll container back to it.
    const pinLayoutRef = useRef({
        columnPinInfo,
        pinnedLeftWidth,
        pinnedRightWidth,
    });
    pinLayoutRef.current = {
        columnPinInfo,
        pinnedLeftWidth,
        pinnedRightWidth,
    };

    // Auto-scroll to keep selected cell visible, accounting for sticky pinned columns.
    // `scrollIntoView` does not know about sticky columns so we compute the
    // scroll position manually when any column is pinned.
    useEffect(() => {
        if (selectedCell && !editingCell) {
            const cellKey = `${selectedCell.row}-${selectedCell.col}`;
            const cellElement = cellRefsMap.current.get(cellKey);
            const container = gridRef.current;

            if (!cellElement || !container) {
                return;
            }

            const layout = pinLayoutRef.current;
            const hasPinned =
                layout.pinnedLeftWidth > 0 || layout.pinnedRightWidth > 0;
            const pinInfo = layout.columnPinInfo.get(selectedCell.col);
            const isCellPinned =
                pinInfo?.isPinnedLeft || pinInfo?.isPinnedRight;

            if (!hasPinned || isCellPinned) {
                // No sticky columns, or the selected cell itself is pinned —
                // the browser's native behaviour is sufficient.
                cellElement.scrollIntoView({
                    block: "nearest",
                    inline: "nearest",
                    behavior: "smooth",
                });
                return;
            }

            // Manual horizontal scroll that accounts for the sticky column widths.
            const containerRect = container.getBoundingClientRect();
            const cellRect = cellElement.getBoundingClientRect();

            // Visible scroll area starts after pinned-left columns and ends
            // before pinned-right columns.
            const visibleLeft = containerRect.left + layout.pinnedLeftWidth;
            const visibleRight = containerRect.right - layout.pinnedRightWidth;

            const cellLeft = cellRect.left;
            const cellRight = cellRect.right;

            if (cellLeft < visibleLeft) {
                // Cell is obscured by left-pinned columns — scroll left.
                container.scrollLeft -= visibleLeft - cellLeft;
            } else if (cellRight > visibleRight) {
                // Cell is obscured by right-pinned columns — scroll right.
                container.scrollLeft += cellRight - visibleRight;
            }

            // Vertical scroll: delegate to the browser.
            cellElement.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
    }, [selectedCell, editingCell]);

    const registerCellRef = useCallback(
        (rowIdx: number, colIdx: number, element: HTMLDivElement | null) => {
            const cellKey = `${rowIdx}-${colIdx}`;
            if (element) {
                cellRefsMap.current.set(cellKey, element);
            } else {
                cellRefsMap.current.delete(cellKey);
            }
        },
        [],
    );

    const handleCellChange = useCallback(
        (rowIdx: number, colIdx: number, value: any) => {
            if (expandable) {
                return;
            }

            const { field } = columns[colIdx];
            const newData = data.map((row, idx) =>
                idx === rowIdx ? { ...row, [field]: value } : row,
            );

            if (rowIdx >= data.length) {
                range(data.length, rowIdx).forEach(() => {
                    newData.push({ [field]: null });
                });
                newData.push({ [field]: value });
            }

            handleChange(newData, [[rowIdx, colIdx]]);
        },
        [data, handleChange, columns, expandable],
    );

    const enterEditMode = useCallback(
        (
            rowIdx: number,
            colIdx: number,
            enterEditMoveEvent: React.KeyboardEvent | React.MouseEvent,
        ) => {
            if (expandable) {
                return;
            }

            // Prevent editing empty spare rows
            if (emptySpareRows && rowIdx >= data.length) {
                return;
            }
            const column = columns[colIdx];
            const field = column.field;
            const value = data[rowIdx]?.[field];

            if (isCellEditable && !isCellEditable(rowIdx, colIdx)) {
                return;
            }

            if (
                column.isEditable === false ||
                (isFunction(column.isEditable) &&
                    !column.isEditable({ row: rowIdx, col: colIdx }))
            ) {
                return;
            }

            setEditingCell({ row: rowIdx, col: colIdx });
            setEditedCellValue(value);
            setEnterEditModeKey(
                "key" in enterEditMoveEvent ? enterEditMoveEvent.key : null,
            );

            onEnterEditMode?.();
        },
        [
            data,
            columns,
            isCellEditable,
            onEnterEditMode,
            emptySpareRows,
            expandable,
        ],
    );

    const exitEditMode = useCallback(
        (params?: { value: CellValue; rowIdx: number; colIdx: number }) => {
            setEditingCell((currentEditingCell) => {
                setEditedCellValue((currentEditedCellValue: any) => {
                    if (params) {
                        handleCellChange(
                            params.rowIdx,
                            params.colIdx,
                            params.value,
                        );
                    } else if (currentEditingCell) {
                        handleCellChange(
                            currentEditingCell.row,
                            currentEditingCell.col,
                            currentEditedCellValue,
                        );
                    }
                    return undefined;
                });

                onExitEditMode?.();
                return null;
            });
            if (gridRef.current) {
                gridRef.current.focus({ preventScroll: true });
            }
        },
        [handleCellChange, onExitEditMode],
    );

    useImperativeHandle(
        gridHandle,
        () => ({
            focus(options) {
                const row = options?.row ?? 0;
                const col = options?.col ?? 0;
                // Commit any in-progress cell edit first so programmatic focus
                // moves never discard what the user has typed.
                if (editingCell) {
                    exitEditMode();
                }
                setSelectedCell({ row, col });
                setSelectionRange({ start: null, end: null, selecting: false });
                gridRef.current?.focus({ preventScroll: true });
            },
        }),
        [editingCell, exitEditMode],
    );

    const clearSelectedCells = useCallback(() => {
        if (expandable) {
            return;
        }

        const selectionActive =
            selectionRange.start &&
            selectionRange.end &&
            (selectionRange.start.row !== selectionRange.end.row ||
                selectionRange.start.col !== selectionRange.end.col);

        if (!selectedCell && !selectionActive) {
            return;
        }

        if (selectionActive && selectionRange.start && selectionRange.end) {
            // Clear all selected cells
            const rowMin = Math.min(
                selectionRange.start.row,
                selectionRange.end.row,
            );
            // Clamp to actual data rows — spare rows beyond data.length are
            // display-only and cannot be cleared.
            const rowMax = Math.min(
                Math.max(selectionRange.start.row, selectionRange.end.row),
                data.length - 1,
            );
            const colMin = Math.min(
                selectionRange.start.col,
                selectionRange.end.col,
            );
            const colMax = Math.max(
                selectionRange.start.col,
                selectionRange.end.col,
            );
            let newData = [...data];
            const changedCells: ChangedCells = [];
            for (let r = rowMin; r <= rowMax; r++) {
                for (let c = colMin; c <= colMax; c++) {
                    newData = newData.map((row, idx) =>
                        idx === r
                            ? { ...row, [columns[c].field]: undefined }
                            : row,
                    );
                    changedCells.push([r, c]);
                }
            }
            handleChange(newData, changedCells);
        } else if (selectedCell) {
            handleCellChange(selectedCell.row, selectedCell.col, undefined);
        }
    }, [
        selectionRange,
        data,
        columns,
        selectedCell,
        handleCellChange,
        handleChange,
        expandable,
    ]);

    const handleShiftArrow = useCallback(
        (key: string) => {
            if (!selectedCell) {
                return;
            }

            // Anchor is where selection started, focus is where it ends
            const anchor = selectionRange.start ?? selectedCell;
            const focus = selectionRange.end ?? selectedCell;
            const newFocus = { ...focus };
            if (key === "ArrowDown") {
                newFocus.row = Math.min(focus.row + 1, renderedData.length - 1);
            }
            if (key === "ArrowUp") {
                newFocus.row = Math.max(focus.row - 1, 0);
            }
            if (key === "ArrowLeft") {
                newFocus.col = clampSelectableColumnIndex(
                    columns,
                    focus.col - 1,
                );
            }
            if (key === "ArrowRight") {
                newFocus.col = clampSelectableColumnIndex(
                    columns,
                    focus.col + 1,
                );
            }
            setSelectedCell(newFocus);
            setSelectionRange({
                start: anchor,
                end: newFocus,
                selecting: false,
            });
        },
        [
            renderedData,
            columns,
            selectedCell,
            selectionRange,
            setSelectedCell,
            setSelectionRange,
        ],
    );

    // Move to next cell on Tab (horizontally, wrapping to next row)
    // Returns true if preventDefault should be called, false otherwise
    const moveToNextCell = useCallback(
        (selectedRow: number, selectedCol: number, step = 1): boolean => {
            const nextCol = findNextSelectableColumnIndex(
                columns,
                selectedCol,
                step,
            );
            if (nextCol !== null) {
                setSelectedCell({ row: selectedRow, col: nextCol });
                setSelectionRange({
                    start: null,
                    end: null,
                    selecting: false,
                });
                return true;
            }
            const nextRow = selectedRow + step;
            if (nextRow >= 0 && nextRow < renderedData.length) {
                setSelectedCell({
                    row: nextRow,
                    col: step > 0 ? 0 : getLastSelectableColumnIndex(columns),
                });
                setSelectionRange({
                    start: null,
                    end: null,
                    selecting: false,
                });
                return true;
            }
            setSelectedCell(null);
            setSelectionRange({
                start: null,
                end: null,
                selecting: false,
            });
            return false;
        },
        [columns, renderedData.length],
    );

    // Header content and its portaled popups (the column gear) own their keys;
    // stopping them here would also hide them from dnd-kit's window listener.
    const headerKeyDownRef = useRef<KeyboardEvent | null>(null);
    const markHeaderKeyDown = useCallback((e: React.KeyboardEvent) => {
        headerKeyDownRef.current = e.nativeEvent;
    }, []);

    // Keyboard navigation and edit
    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (e.nativeEvent === headerKeyDownRef.current) {
                return;
            }
            if (e.metaKey && e.key === "Enter") {
                // This should be handled by hotkey commands to submit the form
                return;
            }

            if (e.key !== "Escape" || editingCell) {
                e.stopPropagation();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === "c") {
                handleCopy(e);

                return;
            }

            if ((e.ctrlKey || e.metaKey) && e.key === "v") {
                return;
            }

            if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
                if (e.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }

                return;
            }

            if (!selectedCell) {
                return;
            }
            const { row: selectedRow, col: selectedCol } = selectedCell;
            if (editingCell) {
                if (e.key === "Enter") {
                    exitEditMode();
                    e.preventDefault();
                } else if (e.key === "Escape") {
                    exitEditMode({
                        value: flatData[selectedRow]?.[
                            columns[selectedCol].field
                        ],
                        rowIdx: selectedRow,
                        colIdx: selectedCol,
                    });
                    e.preventDefault();
                } else if (e.key === "Tab") {
                    exitEditMode();
                    const shouldPreventDefault = moveToNextCell(
                        selectedRow,
                        selectedCol,
                        e.shiftKey ? -1 : 1,
                    );
                    if (shouldPreventDefault) {
                        e.preventDefault();
                    }
                } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    exitEditMode();
                    const newRow =
                        e.key === "ArrowUp"
                            ? Math.max(selectedRow - 1, 0)
                            : Math.min(
                                  selectedRow + 1,
                                  renderedData.length - 1,
                              );
                    setSelectedCell({ row: newRow, col: selectedCol });
                    setSelectionRange({
                        start: null,
                        end: null,
                        selecting: false,
                    });
                    e.preventDefault();
                }
                return;
            }

            // --- Shift+Arrow support for range selection ---
            const isShift = e.shiftKey;
            if (
                isShift &&
                ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(
                    e.key,
                )
            ) {
                handleShiftArrow(e.key);
                e.preventDefault();
                return;
            }
            // --- End Shift+Arrow support ---

            // Tab navigation: move to next cell horizontally, wrap to next row
            if (e.key === "Tab") {
                const shouldPreventDefault = moveToNextCell(
                    selectedRow,
                    selectedCol,
                    e.shiftKey ? -1 : 1,
                );
                if (shouldPreventDefault) {
                    e.preventDefault();
                }
                return;
            }

            if (e.key === "Enter") {
                enterEditMode(selectedCell.row, selectedCell.col, e);
                // Cancel selection range
                setSelectionRange({ start: null, end: null, selecting: false });
                e.preventDefault();
            } else if (e.key === "ArrowDown") {
                if (
                    selectedRow === renderedData.length - 1 &&
                    onNavigatePastEnd
                ) {
                    setSelectedCell(null);
                    onNavigatePastEnd(selectedCol);
                } else {
                    setSelectedCell({
                        row: Math.min(selectedRow + 1, renderedData.length - 1),
                        col: selectedCol,
                    });
                }
                setSelectionRange({
                    start: null,
                    end: null,
                    selecting: false,
                });
                e.preventDefault();
            } else if (e.key === "ArrowUp") {
                if (selectedRow === 0 && onNavigatePastStart) {
                    setSelectedCell(null);
                    onNavigatePastStart(selectedCol);
                } else {
                    setSelectedCell({
                        row: Math.max(selectedRow - 1, 0),
                        col: selectedCol,
                    });
                }
                setSelectionRange({
                    start: null,
                    end: null,
                    selecting: false,
                });
                e.preventDefault();
            } else if (e.key === "ArrowLeft") {
                setSelectedCell({
                    row: selectedRow,
                    col: clampSelectableColumnIndex(columns, selectedCol - 1),
                });
                setSelectionRange({ start: null, end: null, selecting: false });
                e.preventDefault();
            } else if (e.key === "ArrowRight") {
                setSelectedCell({
                    row: selectedRow,
                    col: clampSelectableColumnIndex(columns, selectedCol + 1),
                });
                setSelectionRange({ start: null, end: null, selecting: false });
                e.preventDefault();
            } else if (e.key === "Backspace" || e.key === "Delete") {
                clearSelectedCells();
                e.preventDefault();
            } else if (e.key.length === 1) {
                // Start editing on any character key
                enterEditMode(selectedCell.row, selectedCell.col, e);
                setSelectionRange({ start: null, end: null, selecting: false });
            }
        },
        [
            selectedCell,
            editingCell,
            renderedData,
            columns,
            exitEditMode,
            enterEditMode,
            clearSelectedCells,
            handleShiftArrow,
            handleCopy,
            moveToNextCell,
            handleUndo,
            handleRedo,
            flatData,
            onNavigatePastEnd,
            onNavigatePastStart,
        ],
    );

    // Focus grid when a cell is selected by mouse
    const focusGrid = useCallback(() => {
        if (gridRef.current) {
            gridRef.current.focus({ preventScroll: true });
        }
    }, []);

    const isActionsColumnTarget = useCallback(
        (target: EventTarget | null) =>
            (target as HTMLElement | null)?.closest(
                `.${styles.actionsCell}`,
            ) !== null,
        [],
    );

    const isResizerTarget = useCallback(
        (target: EventTarget | null) =>
            target instanceof Element &&
            target.closest(`.${styles.resizer}`) !== null,
        [],
    );

    const mouseDownInActionsRef = useRef(false);

    const handleGridMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!gridRef.current?.contains(e.target as Node)) {
                return;
            }
            const isInActions = isActionsColumnTarget(e.target);
            mouseDownInActionsRef.current = isInActions;
            if (isInActions || isResizerTarget(e.target)) {
                return;
            }
            focusGrid();
        },
        [focusGrid, isActionsColumnTarget, isResizerTarget],
    );

    const handleFocus = useCallback(
        (e: React.FocusEvent) => {
            if (
                isActionsColumnTarget(e.target) ||
                mouseDownInActionsRef.current
            ) {
                return;
            }
            if (
                !selectedCell &&
                !editingCell &&
                renderedData.length > 0 &&
                columns.length > 0
            ) {
                setSelectedCell(
                    (previousSelectedCell) =>
                        previousSelectedCell ?? { row: 0, col: 0 },
                );
                setSelectionRange({
                    start: null,
                    end: null,
                    selecting: false,
                });
            }
        },
        [
            selectedCell,
            editingCell,
            renderedData.length,
            columns.length,
            isActionsColumnTarget,
        ],
    );

    const emptySelection = useCallback(() => {
        setSelectedCell(null);
        setSelectionRange({ start: null, end: null, selecting: false });
    }, []);

    // Handle blur event when focus leaves the grid
    const handleBlur = useCallback(
        (e: React.FocusEvent) => {
            if (
                isActionsColumnTarget(e.relatedTarget) ||
                mouseDownInActionsRef.current
            ) {
                return;
            }
            if (!editingCell && clearSelectionOnBlur) {
                emptySelection();
            }
        },
        [
            editingCell,
            emptySelection,
            isActionsColumnTarget,
            clearSelectionOnBlur,
        ],
    );

    // Mouse event handlers for drag selection
    const handleCellMouseDown = useCallback(
        (e: React.MouseEvent, rowIdx: number, colIdx: number) => {
            if (isSpreadsheetActionsColumn(columns[colIdx])) {
                return;
            }
            e.preventDefault();
            mouseDownRef.current = true;
            mouseDownPositionRef.current = { x: e.clientX, y: e.clientY };
            hasDraggedRef.current = false;
            if (editingCell?.row === rowIdx && editingCell?.col === colIdx) {
                return;
            }

            // Handle Shift+Click for range selection
            if (e.shiftKey && selectedCell) {
                // Anchor is where selection started, or the currently selected cell if no range exists
                const anchor = selectionRange.start ?? selectedCell;
                const clickedCell = { row: rowIdx, col: colIdx };

                setSelectedCell(clickedCell);
                setSelectionRange({
                    start: anchor,
                    end: clickedCell,
                    selecting: false,
                });
                focusGrid();
                return;
            }

            // Normal click: clear selection range and select single cell
            setSelectedCell({ row: rowIdx, col: colIdx });
            exitEditMode();
            setSelectionRange({ start: null, end: null, selecting: false });
            focusGrid();
        },
        [
            focusGrid,
            editingCell,
            exitEditMode,
            selectedCell,
            selectionRange,
            columns,
        ],
    );

    // Drag fill handle mouse events
    const handleFillHandleMouseDown = useCallback(
        (e: React.MouseEvent, rowIdx: number, colIdx: number) => {
            e.stopPropagation();
            setFillMode({
                isFilling: true,
                start: { row: rowIdx, col: colIdx },
                end: { row: rowIdx, col: colIdx },
            });
            mouseDownRef.current = false; // Prevent normal drag selection
            document.body.style.cursor = "crosshair";
        },
        [],
    );

    const handleCellMouseEnter = useCallback(
        (rowIdx: number, colIdx: number) => {
            if (fillMode.isFilling && fillMode.start) {
                setFillMode((fm) => ({
                    ...fm,
                    end: { row: rowIdx, col: colIdx },
                }));
                return;
            }
            if (!mouseDownRef.current || !hasDraggedRef.current) {
                return;
            }
            // If starting drag, initialize selectionRange
            if (!selectionRange.selecting) {
                setSelectionRange({
                    start: selectedCell,
                    end: { row: rowIdx, col: colIdx },
                    selecting: true,
                });
            } else {
                setSelectionRange((currentRange) => ({
                    ...currentRange,
                    end: { row: rowIdx, col: colIdx },
                }));
            }
        },
        [fillMode, selectionRange.selecting, selectedCell],
    );

    const handleMouseUp = useCallback(() => {
        const fm = fillModeRef.current;
        if (fm.isFilling && fm.start && fm.end) {
            // --- Enhanced drag-fill: support multi-cell source range ---
            // Determine source range (from selection if active, else just the fill start cell)
            let srcStartRow = fm.start.row;
            let srcEndRow = fm.start.row;
            let srcStartCol = fm.start.col;
            let srcEndCol = fm.start.col;
            if (
                selectionRange.start &&
                selectionRange.end &&
                (selectionRange.start.row !== selectionRange.end.row ||
                    selectionRange.start.col !== selectionRange.end.col)
            ) {
                srcStartRow = Math.min(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                srcEndRow = Math.max(
                    selectionRange.start.row,
                    selectionRange.end.row,
                );
                srcStartCol = Math.min(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );
                srcEndCol = Math.max(
                    selectionRange.start.col,
                    selectionRange.end.col,
                );
            }

            // Target range is from the source range to the fill endpoint
            const tgtStartRow = Math.min(srcStartRow, fm.end.row);
            const tgtEndRow = Math.max(srcEndRow, fm.end.row);
            const tgtStartCol = Math.min(srcStartCol, fm.end.col);
            const tgtEndCol = Math.max(srcEndCol, fm.end.col);

            const srcRowCount = srcEndRow - srcStartRow + 1;
            const srcColCount = srcEndCol - srcStartCol + 1;

            const newData = [...data];
            const changedCells: ChangedCells = [];
            for (let r = tgtStartRow; r <= tgtEndRow; r++) {
                for (let c = tgtStartCol; c <= tgtEndCol; c++) {
                    // Only fill cells outside the source range
                    const inSource =
                        r >= srcStartRow &&
                        r <= srcEndRow &&
                        c >= srcStartCol &&
                        c <= srcEndCol;
                    if (inSource) {
                        continue;
                    }
                    // Never write a locked target cell (e.g. a billable-expense
                    // line's quantity/rate) — mirror the copy/paste guard so
                    // drag-fill can't bypass a column's isEditable predicate.
                    const targetColumn = columns[c];
                    if (
                        isSpreadsheetActionsColumn(targetColumn) ||
                        targetColumn.isEditable === false ||
                        (isFunction(targetColumn.isEditable) &&
                            !targetColumn.isEditable({ row: r, col: c })) ||
                        (isCellEditable && !isCellEditable(r, c))
                    ) {
                        continue;
                    }
                    // Repeat source range as needed
                    const srcR =
                        srcStartRow + ((r - tgtStartRow) % srcRowCount);
                    const srcC =
                        srcStartCol + ((c - tgtStartCol) % srcColCount);
                    const field = targetColumn.field;
                    const value = data[srcR]?.[columns[srcC].field];
                    newData[r] = {
                        ...newData[r],
                        [field]: value,
                    };
                    changedCells.push([r, c]);
                }
            }
            handleChange(newData, changedCells);
            setFillMode({ isFilling: false, start: null, end: null });
            document.body.style.cursor = "";
            return;
        }
        if (selectionRange.selecting) {
            setSelectionRange((currentRange) => ({
                ...currentRange,
                selecting: false,
            }));
            focusGrid();
        }
        mouseDownRef.current = false;
    }, [
        data,
        columns,
        handleChange,
        selectionRange,
        focusGrid,
        isCellEditable,
    ]);

    useEffect(() => {
        if (selectionRange.selecting || fillMode.isFilling) {
            window.addEventListener("mouseup", handleMouseUp);
            return () => window.removeEventListener("mouseup", handleMouseUp);
        }
    }, [selectionRange.selecting, fillMode.isFilling, handleMouseUp]);

    useEffect(() => {
        const handleWindowMouseUp = () => {
            mouseDownRef.current = false;
            mouseDownInActionsRef.current = false;
            mouseDownPositionRef.current = null;
            hasDraggedRef.current = false;
            handleMouseUp();
        };
        window.addEventListener("mouseup", handleWindowMouseUp);
        return () => window.removeEventListener("mouseup", handleWindowMouseUp);
    }, [handleMouseUp]);

    useEffect(() => {
        const handleWindowMouseMove = (e: MouseEvent) => {
            if (
                !mouseDownRef.current ||
                hasDraggedRef.current ||
                !mouseDownPositionRef.current
            ) {
                return;
            }
            const dx = Math.abs(e.clientX - mouseDownPositionRef.current.x);
            const dy = Math.abs(e.clientY - mouseDownPositionRef.current.y);
            if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
                hasDraggedRef.current = true;
            }
        };
        window.addEventListener("mousemove", handleWindowMouseMove);
        return () =>
            window.removeEventListener("mousemove", handleWindowMouseMove);
    }, []);

    const rangeActionCell = useMemo(
        () =>
            rangeAction && !selectionRange.selecting && !editingCell
                ? getRangeActionCell({ selectedCell, selectionRange })
                : null,
        [rangeAction, selectionRange, editingCell, selectedCell],
    );

    const [removedRowIndex, setRemovedRowIndex] = useState<number | null>(null);
    const handleRemoveRow = useCallback(
        (idx: number) => {
            setRemovedRowIndex(idx);
            setEditingCell(null);
            emptySelection();
            exitEditMode();

            const newData = [...data];
            newData[idx] = {};
            handleChange(
                newData,
                columns.map((_, i) => [idx, i]),
            );
        },
        [
            setEditingCell,
            emptySelection,
            data,
            handleChange,
            columns,
            exitEditMode,
        ],
    );

    const [addedRowIndex, setAddedRowIndex] = useState<number | null>(null);
    const handleAddRow = useCallback(
        (idx: number) => {
            setAddedRowIndex(idx);
            setEditingCell(null);
            emptySelection();
            exitEditMode();

            const newData = [...data];
            newData.splice(idx, 0, {});
            handleChange(
                newData,
                columns.map((_, i) => [idx, i]),
            );
        },
        [
            data,
            handleChange,
            setEditingCell,
            emptySelection,
            columns,
            exitEditMode,
        ],
    );

    const headerFiller = centerColumns && (
        <div
            className={classNames(styles.headerFiller, {
                [styles.stickyHeader]: stickyHeader,
            })}
            aria-hidden
        />
    );
    const totalFiller = centerColumns && (
        <div className={styles.totalFiller} aria-hidden />
    );

    const grid = (
        <div
            className={classNames(styles.spreadsheetGrid, className, {
                [styles.noOuterBorder]: noOuterBorder,
                [styles.hasTotalsRow]: showTotalsRow,
                [styles.rowHoverHighlight]: rowHoverHighlight,
                [styles.lightNotEditableCells]: lightNotEditableCells,
                [styles.readonly]: readonly,
            })}
            style={{
                gridTemplateColumns: gridTrackColumns,
                overflow: hasPinnedColumns ? "auto" : overflow,
            }}
            tabIndex={0}
            ref={attachGridElement}
            onPaste={expandable ? undefined : handlePaste}
            onKeyDown={handleKeyDown}
            onMouseDown={handleGridMouseDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            role="grid"
            data-appearance={appearance}
        >
            {!hideHeaderRow && (
                <>
                    {prependHeader}
                    {headerFiller}
                    {columns.map((col, index) => {
                        const pinInfo = columnPinInfo.get(index);
                        // Omit zIndex from pinnedStyle — header cells get their
                        // z-index from CSS (.stickyHeader / .pinnedLeftHeader etc.)
                        const headerPinStyle = pinInfo?.pinnedStyle
                            ? {
                                  ...pinInfo.pinnedStyle,
                                  zIndex: undefined,
                              }
                            : undefined;
                        const header =
                            resizeTable.getHeaderGroups()[0]?.headers[index];
                        const canResizeColumn = canResizeSpreadsheetHeader(
                            header,
                            enableColumnResizing,
                        );
                        return (
                            <div
                                key={col.field}
                                data-header-cell=""
                                className={classNames(
                                    styles.headerCell,
                                    col.headerClassName,
                                    {
                                        [styles.stickyHeader]: stickyHeader,
                                        [styles.lastInRow]:
                                            index === columns.length - 1,
                                        [styles.pinnedLeftHeader]:
                                            pinInfo?.isPinnedLeft,
                                        [styles.pinnedRightHeader]:
                                            pinInfo?.isPinnedRight,
                                        [styles.lastPinnedLeft]:
                                            pinInfo?.isLastPinnedLeft,
                                        [styles.firstPinnedRight]:
                                            pinInfo?.isFirstPinnedRight,
                                    },
                                )}
                                style={headerPinStyle}
                                onKeyDown={markHeaderKeyDown}
                                data-align={col.headerAlign}
                                data-tree={
                                    (hasTreeHeaderLead && index === 0) ||
                                    undefined
                                }
                            >
                                {hasTreeHeaderLead && index === 0 && (
                                    <SpreadsheetTreeToggle
                                        isExpanded={isSomeRowExpanded}
                                        onToggle={toggleAllExpanded}
                                        label={
                                            isSomeRowExpanded
                                                ? "Collapse all rows"
                                                : "Expand all rows"
                                        }
                                    />
                                )}
                                {col.label}
                                {canResizeColumn && (
                                    <div
                                        onDoubleClick={() => {
                                            resetColumnWidth(index);
                                        }}
                                        onMouseDown={header.getResizeHandler()}
                                        onTouchStart={header.getResizeHandler()}
                                        className={classNames(styles.resizer, {
                                            [styles.isResizing]:
                                                header.column.getIsResizing(),
                                        })}
                                    />
                                )}
                            </div>
                        );
                    })}
                    {headerFiller}
                </>
            )}
            {renderedData.map((row, rowIdx) => (
                <SpreadsheetGridRow
                    data={flatData}
                    addedRowIndex={addedRowIndex}
                    removedRowIndex={removedRowIndex}
                    setRemovedRowIndex={setRemovedRowIndex}
                    setAddedRowIndex={setAddedRowIndex}
                    handleChange={handleChange}
                    key={rowIdx}
                    columns={columns}
                    columnPinInfo={columnPinInfo}
                    className={getRowClassName?.(rowIdx) ?? rowClassName}
                    selectedCell={selectedCell}
                    rowIdx={rowIdx}
                    selectionRange={selectionRange}
                    editingCell={editingCell}
                    row={row}
                    editedCellValue={editedCellValue}
                    exitEditMode={exitEditMode}
                    setEditedCellValue={setEditedCellValue}
                    handleRemoveRow={handleRemoveRow}
                    handleAddRow={handleAddRow}
                    handleCellMouseDown={handleCellMouseDown}
                    handleCellMouseEnter={handleCellMouseEnter}
                    handleFillHandleMouseDown={handleFillHandleMouseDown}
                    fillMode={fillMode}
                    isFirstRow={rowIdx === 0}
                    isLastRow={rowIdx === renderedData.length - 1}
                    enterEditMode={enterEditMode}
                    registerCellRef={registerCellRef}
                    enterEditModeKey={enterEditModeKey}
                    disableDragFill={disableDragFill}
                    showDropdownIndicator={showDropdownIndicator}
                    isEmptyRow={emptySpareRows && rowIdx >= flatData.length}
                    isCellEditable={isCellEditable}
                    expandable={expandable}
                    nestingIndentPx={nestingIndentPx}
                    expandedIds={expandedIds}
                    onToggleExpand={toggleExpanded}
                    onRowClick={onRowClick}
                    rangeAction={
                        rangeActionCell?.row === rowIdx
                            ? rangeAction
                            : undefined
                    }
                    rangeActionCol={rangeActionCell?.col ?? null}
                    appearance={appearance}
                    centerColumns={centerColumns}
                />
            ))}
            {showTotalsRow && (
                <>
                    {totalFiller}
                    {columns.map((col, index) => {
                        const pinInfo = columnPinInfo.get(index);
                        // Omit zIndex from pinnedStyle — total cells get their
                        // z-index from CSS (.totalCell / .pinnedLeftTotal etc.)
                        const totalPinStyle = pinInfo?.pinnedStyle
                            ? {
                                  ...pinInfo.pinnedStyle,
                                  zIndex: undefined,
                              }
                            : undefined;
                        return (
                            <div
                                key={col.field}
                                className={classNames(styles.totalCell, {
                                    [styles.lastInRow]:
                                        index === columns.length - 1,
                                    [styles.pinnedLeftTotal]:
                                        pinInfo?.isPinnedLeft,
                                    [styles.pinnedRightTotal]:
                                        pinInfo?.isPinnedRight,
                                    [styles.lastPinnedLeft]:
                                        pinInfo?.isLastPinnedLeft,
                                    [styles.firstPinnedRight]:
                                        pinInfo?.isFirstPinnedRight,
                                })}
                                style={totalPinStyle}
                            >
                                {col.aggregation && col.aggregation(data)}
                            </div>
                        );
                    })}
                    {totalFiller}
                </>
            )}
        </div>
    );

    return floatingFooter ? (
        <SpreadsheetFloatingFooterHost
            footer={floatingFooter}
            scrollbarSizes={scrollbarSizes}
        >
            {grid}
        </SpreadsheetFloatingFooterHost>
    ) : (
        grid
    );
};

export const SpreadsheetGrid: React.FC<SpreadsheetGridProps> = (props) => (
    <SpreadsheetColumnResizeScope
        retainedColumnIds={props.columns.map(getSpreadsheetColumnResizeId)}
    >
        <SpreadsheetGridContent {...props} />
    </SpreadsheetColumnResizeScope>
);
